// repo/primary-care-connect/frontend/src/pages/Portal/FamilyDoctor/Profile/components/IntakeRulesSection.tsx
import React, { useEffect, useMemo, useState } from "react";

import {
  ApiError,
  FamilyDoctorIntakeRule,
  updateFamilyDoctorIntakeRules,
} from "../../../../../api/portalFamilyDoctor";

type IntakeRulesSectionProps = {
  editMode: boolean;
  intakeRules: FamilyDoctorIntakeRule[];
  onSaved: (rules: FamilyDoctorIntakeRule[]) => void;
  formatDateTime: (val: string | null | undefined) => string;
};

type AgeGroupKey =
  | "InfantUnder1"
  | "Children"
  | "Adults"
  | "Seniors65"
  | "Seniors75";

type AgeGroupPreference = "Comfortable" | "StrongFit" | "";

type ComplexityComfort =
  | ""
  | "StandardOnly"
  | "Moderate"
  | "ComfortableWithComplex"
  | "DiscussComplexCasesFirst";

type GeographyPreference =
  | ""
  | "NoPreference"
  | "SameFSAPreferred"
  | "SameCityPreferred"
  | "ReasonableDistancePreferred";

type IntakePreferences = {
  ageGroups: Record<AgeGroupKey, AgeGroupPreference>;
  complexityComfort: ComplexityComfort;
  clinicalComfortAreas: string[];
  geographyPreference: GeographyPreference;
  navigatorNote: string;
};

const AGE_GROUP_OPTIONS: Array<{ key: AgeGroupKey; label: string }> = [
  { key: "InfantUnder1", label: "Newborn / infant under 1" },
  { key: "Children", label: "Children" },
  { key: "Adults", label: "Adults" },
  { key: "Seniors65", label: "Seniors 65+" },
  { key: "Seniors75", label: "Seniors 75+" },
];

const AGE_GROUP_PREFERENCE_OPTIONS: Array<{
  value: AgeGroupPreference;
  label: string;
}> = [
  { value: "", label: "No preference" },
  { value: "Comfortable", label: "Comfortable" },
  { value: "StrongFit", label: "Strong fit" },
];

const COMPLEXITY_OPTIONS: Array<{ value: ComplexityComfort; label: string }> = [
  { value: "", label: "No preference" },
  { value: "StandardOnly", label: "Standard complexity" },
  { value: "Moderate", label: "Moderate complexity" },
  { value: "ComfortableWithComplex", label: "Comfortable with complex patients" },
  {
    value: "DiscussComplexCasesFirst",
    label: "Discuss complex cases with clinic first",
  },
];

const CLINICAL_COMFORT_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "ChronicDiseaseDiabetes", label: "Chronic disease / diabetes" },
  { value: "FrailSeniors", label: "Frail seniors" },
  { value: "MentalHealth", label: "Mental health" },
  { value: "NewbornCare", label: "Newborn care" },
  { value: "WomensHealth", label: "Women’s health" },
  { value: "PrenatalPostpartum", label: "Prenatal / postpartum" },
  { value: "PalliativeCare", label: "Palliative care" },
  { value: "DisabilityAccessibilityNeeds", label: "Disability / accessibility needs" },
];

const GEOGRAPHY_OPTIONS: Array<{ value: GeographyPreference; label: string }> = [
  { value: "", label: "No preference" },
  { value: "NoPreference", label: "No geographic preference" },
  { value: "SameFSAPreferred", label: "Same FSA preferred" },
  { value: "SameCityPreferred", label: "Same city preferred" },
  {
    value: "ReasonableDistancePreferred",
    label: "Within reasonable driving distance preferred",
  },
];

function createEmptyPreferences(): IntakePreferences {
  return {
    ageGroups: {
      InfantUnder1: "",
      Children: "",
      Adults: "",
      Seniors65: "",
      Seniors75: "",
    },
    complexityComfort: "",
    clinicalComfortAreas: [],
    geographyPreference: "",
    navigatorNote: "",
  };
}

function safeString(v: any): string | null {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s.length ? s : null;
}

function parseJsonObject(raw: string | null | undefined): any | null {
  const s = safeString(raw);
  if (!s) return null;

  try {
    const parsed = JSON.parse(s);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function parseJsonArray(raw: string | null | undefined): string[] {
  const parsed = parseJsonObject(raw);
  if (Array.isArray(parsed)) {
    return parsed.map((x) => String(x)).filter(Boolean);
  }

  const s = safeString(raw);
  if (!s) return [];

  return s
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
}

function normalizeAgePreference(value: any): AgeGroupPreference {
  const s = safeString(value);
  if (!s) return "";

  if (s === "Comfortable" || s === "StrongFit") return s;

  // Backward compatibility with earlier saved values.
  if (s === "Accept") return "Comfortable";
  if (s === "Prefer") return "StrongFit";

  return "";
}

function normalizeComplexity(value: any): ComplexityComfort {
  const s = safeString(value);
  if (
    s === "StandardOnly" ||
    s === "Moderate" ||
    s === "ComfortableWithComplex" ||
    s === "DiscussComplexCasesFirst"
  ) {
    return s;
  }

  // Backward compatibility with earlier saved value.
  if (s === "AvoidHighComplexity") return "DiscussComplexCasesFirst";

  return "";
}

function normalizeGeography(value: any): GeographyPreference {
  const s = safeString(value);
  if (
    s === "NoPreference" ||
    s === "SameFSAPreferred" ||
    s === "SameCityPreferred" ||
    s === "ReasonableDistancePreferred"
  ) {
    return s;
  }

  return "";
}

function getLabel(
  options: Array<{ value: string; label: string }>,
  value: string | null | undefined
) {
  const s = safeString(value);
  if (!s) return "—";
  return options.find((x) => x.value === s)?.label || s;
}

function getAgePreferenceLabel(value: AgeGroupPreference) {
  return (
    AGE_GROUP_PREFERENCE_OPTIONS.find((x) => x.value === value)?.label ||
    "No preference"
  );
}

function getClinicalComfortLabel(value: string) {
  return CLINICAL_COMFORT_OPTIONS.find((x) => x.value === value)?.label || value;
}

function intakeRulesToPreferences(rules: FamilyDoctorIntakeRule[]): IntakePreferences {
  const next = createEmptyPreferences();

  for (const rule of rules || []) {
    const key = safeString(rule.criterionKey);
    if (!key) continue;

    if (key === "ageGroups") {
      const parsed = parseJsonObject(rule.criterionValue);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        for (const opt of AGE_GROUP_OPTIONS) {
          next.ageGroups[opt.key] = normalizeAgePreference(parsed[opt.key]);
        }
      }
      continue;
    }

    if (key === "complexityComfort") {
      next.complexityComfort = normalizeComplexity(rule.criterionValue);
      continue;
    }

    if (key === "clinicalComfortAreas") {
      next.clinicalComfortAreas = parseJsonArray(rule.criterionValue).filter((x) =>
        CLINICAL_COMFORT_OPTIONS.some((opt) => opt.value === x)
      );
      continue;
    }

    if (key === "geographyPreference") {
      next.geographyPreference = normalizeGeography(rule.criterionValue);
      continue;
    }

    if (key === "navigatorNote") {
      next.navigatorNote = safeString(rule.criterionValue) || "";
      continue;
    }
  }

  return next;
}

function preferencesToRules(preferences: IntakePreferences) {
  const rules: Array<{
    criterionKey: string;
    criterionValue: string;
    matchBehavior: string;
    weight: number;
    isHardRule: boolean;
    isActive: boolean;
  }> = [];

  const activeAgePrefs = Object.entries(preferences.ageGroups).filter(
    ([, value]) => !!value
  );

  if (activeAgePrefs.length) {
    rules.push({
      criterionKey: "ageGroups",
      criterionValue: JSON.stringify(preferences.ageGroups),
      matchBehavior: "StructuredPreference",
      weight: 0,
      isHardRule: false,
      isActive: true,
    });
  }

  if (preferences.complexityComfort) {
    rules.push({
      criterionKey: "complexityComfort",
      criterionValue: preferences.complexityComfort,
      matchBehavior:
        preferences.complexityComfort === "DiscussComplexCasesFirst"
          ? "Caution"
          : "Preferred",
      weight: preferences.complexityComfort === "DiscussComplexCasesFirst" ? 0 : 10,
      isHardRule: false,
      isActive: true,
    });
  }

  if (preferences.clinicalComfortAreas.length) {
    rules.push({
      criterionKey: "clinicalComfortAreas",
      criterionValue: JSON.stringify(preferences.clinicalComfortAreas),
      matchBehavior: "Preferred",
      weight: 10,
      isHardRule: false,
      isActive: true,
    });
  }

  if (preferences.geographyPreference) {
    rules.push({
      criterionKey: "geographyPreference",
      criterionValue: preferences.geographyPreference,
      matchBehavior: "Preferred",
      weight: 8,
      isHardRule: false,
      isActive: true,
    });
  }

  const note = preferences.navigatorNote.trim();
  if (note) {
    rules.push({
      criterionKey: "navigatorNote",
      criterionValue: note.slice(0, 500),
      matchBehavior: "Note",
      weight: 0,
      isHardRule: false,
      isActive: true,
    });
  }

  return rules;
}

function toggleValue(values: string[], value: string) {
  if (values.includes(value)) {
    return values.filter((x) => x !== value);
  }

  return [...values, value];
}

function SectionHelp({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 6, color: "#6b7a94", fontSize: 13 }}>
      {children}
    </div>
  );
}

function ReadOnlyChipList({
  values,
  getItemLabel,
  emptyText = "No preference set.",
}: {
  values: string[];
  getItemLabel: (value: string) => string;
  emptyText?: string;
}) {
  if (!values.length) {
    return <span style={{ color: "#6b7a94" }}>{emptyText}</span>;
  }

  return (
    <div className="fd-profile__chips">
      {values.map((value) => (
        <span key={value} className="fd-profile__chip">
          {getItemLabel(value)}
        </span>
      ))}
    </div>
  );
}

export default function IntakeRulesSection({
  editMode,
  intakeRules,
  onSaved,
  formatDateTime,
}: IntakeRulesSectionProps) {
  const [preferences, setPreferences] = useState<IntakePreferences>(
    intakeRulesToPreferences(intakeRules)
  );

  const [savingIntakeRules, setSavingIntakeRules] = useState(false);
  const [intakeRulesError, setIntakeRulesError] = useState<string | null>(null);
  const [intakeRulesSavedAt, setIntakeRulesSavedAt] = useState<string | null>(
    null
  );

  useEffect(() => {
    setPreferences(intakeRulesToPreferences(intakeRules));
    setIntakeRulesError(null);
    setIntakeRulesSavedAt(null);
  }, [intakeRules]);

  const hasAnyPreference = useMemo(() => {
    return (
      Object.values(preferences.ageGroups).some(Boolean) ||
      !!preferences.complexityComfort ||
      preferences.clinicalComfortAreas.length > 0 ||
      !!preferences.geographyPreference ||
      !!preferences.navigatorNote.trim()
    );
  }, [preferences]);

  function updateAgeGroupPreference(
    key: AgeGroupKey,
    value: AgeGroupPreference
  ) {
    setPreferences((prev) => ({
      ...prev,
      ageGroups: {
        ...prev.ageGroups,
        [key]: value,
      },
    }));

    setIntakeRulesError(null);
    setIntakeRulesSavedAt(null);
  }

  function updateClinicalComfortArea(value: string) {
    setPreferences((prev) => ({
      ...prev,
      clinicalComfortAreas: toggleValue(prev.clinicalComfortAreas, value),
    }));

    setIntakeRulesError(null);
    setIntakeRulesSavedAt(null);
  }

  async function onSaveIntakeRules() {
    setSavingIntakeRules(true);
    setIntakeRulesError(null);
    setIntakeRulesSavedAt(null);

    try {
      const note = preferences.navigatorNote.trim();
      if (note.length > 500) {
        throw new Error("Navigator note must be 500 characters or less.");
      }

      const rules = preferencesToRules({
        ...preferences,
        navigatorNote: note,
      });

      const res = await updateFamilyDoctorIntakeRules({ rules });

      onSaved(res.intakeRules);
      setPreferences(intakeRulesToPreferences(res.intakeRules));
      setIntakeRulesSavedAt(new Date().toISOString());
    } catch (e: any) {
      if (e instanceof ApiError) {
        setIntakeRulesError(e.body?.error || e.message);
      } else {
        setIntakeRulesError(e?.message || "Failed to save intake rules");
      }
    } finally {
      setSavingIntakeRules(false);
    }
  }

  return (
    <details className="fd-profile__panel" open>
      <summary className="fd-profile__summary">
        <div>
          <div className="fd-profile__sectionTitle">Intake Rules</div>
          <div className="fd-profile__hint">
            Internal structured preferences for Community Navigator matching
          </div>
        </div>
      </summary>

      <div className="fd-profile__row">
        <div className="fd-profile__label">Purpose</div>
        <div className="fd-profile__value" style={{ color: "#6b7a94" }}>
          These preferences help navigators identify patients who may be a strong
          fit for your practice. They are internal only and are not shown to
          patients. Languages, visit types, coverage, accessibility, clinic
          services, capacity, and accepting status are already taken from your
          profile and do not need to be repeated here.
        </div>
      </div>

      {!editMode ? (
        <>
          {!hasAnyPreference ? (
            <div className="fd-profile__row">
              <div className="fd-profile__label">Current rules</div>
              <div className="fd-profile__value" style={{ color: "#6b7a94" }}>
                No intake rules have been set yet.
              </div>
            </div>
          ) : null}

          <div className="fd-profile__row">
            <div className="fd-profile__label">Age groups</div>
            <div className="fd-profile__value">
              <div className="fd-profile__kvlist">
                {AGE_GROUP_OPTIONS.map((option) => {
                  const value = preferences.ageGroups[option.key];
                  return value ? (
                    <div key={option.key} className="fd-profile__kvrow">
                      <div className="fd-profile__kvkey fd-profile__kvkey--wide">
                        {option.label}
                      </div>
                      <div className="fd-profile__kvval">
                        {getAgePreferenceLabel(value)}
                      </div>
                    </div>
                  ) : null;
                })}
                {!Object.values(preferences.ageGroups).some(Boolean) ? (
                  <div style={{ color: "#6b7a94" }}>No preference set.</div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="fd-profile__row">
            <div className="fd-profile__label">Complexity comfort</div>
            <div className="fd-profile__value">
              {getLabel(COMPLEXITY_OPTIONS, preferences.complexityComfort)}
            </div>
          </div>

          <div className="fd-profile__row">
            <div className="fd-profile__label">Clinical comfort areas</div>
            <div className="fd-profile__value">
              <ReadOnlyChipList
                values={preferences.clinicalComfortAreas}
                getItemLabel={getClinicalComfortLabel}
              />
            </div>
          </div>

          <div className="fd-profile__row">
            <div className="fd-profile__label">Geography preference</div>
            <div className="fd-profile__value">
              {getLabel(GEOGRAPHY_OPTIONS, preferences.geographyPreference)}
            </div>
          </div>

          <div className="fd-profile__row">
            <div className="fd-profile__label">Navigator note</div>
            <div className="fd-profile__value">
              {preferences.navigatorNote ? (
                <div style={{ whiteSpace: "pre-wrap" }}>{preferences.navigatorNote}</div>
              ) : (
                <span style={{ color: "#6b7a94" }}>—</span>
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="fd-profile__row">
            <div className="fd-profile__label">Age groups</div>
            <div className="fd-profile__value">
              <div className="fd-profile__kvlist">
                {AGE_GROUP_OPTIONS.map((option) => (
                  <div key={option.key} className="fd-profile__kvrow">
                    <div className="fd-profile__kvkey fd-profile__kvkey--wide">
                      {option.label}
                    </div>
                    <div className="fd-profile__kvval">
                      <select
                        value={preferences.ageGroups[option.key]}
                        disabled={savingIntakeRules}
                        onChange={(e) =>
                          updateAgeGroupPreference(
                            option.key,
                            e.target.value as AgeGroupPreference
                          )
                        }
                        style={{
                          width: "100%",
                          maxWidth: 260,
                          padding: "10px 12px",
                          borderRadius: 12,
                          border: "1px solid #d1d9e6",
                          fontFamily: "inherit",
                        }}
                      >
                        {AGE_GROUP_PREFERENCE_OPTIONS.map((opt) => (
                          <option key={opt.value || "none"} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
              <SectionHelp>
                Choose only where you have a meaningful fit preference. Leave
                blank when there is no special fit issue.
              </SectionHelp>
            </div>
          </div>

          <div className="fd-profile__row">
            <div className="fd-profile__label">Complexity comfort</div>
            <div className="fd-profile__value">
              <select
                value={preferences.complexityComfort}
                disabled={savingIntakeRules}
                onChange={(e) => {
                  setPreferences((prev) => ({
                    ...prev,
                    complexityComfort: e.target.value as ComplexityComfort,
                  }));
                  setIntakeRulesError(null);
                  setIntakeRulesSavedAt(null);
                }}
                style={{
                  width: "100%",
                  maxWidth: 420,
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid #d1d9e6",
                  fontFamily: "inherit",
                }}
              >
                {COMPLEXITY_OPTIONS.map((opt) => (
                  <option key={opt.value || "none"} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <SectionHelp>
                This helps navigators understand whether they should contact the
                clinic before forwarding a more complex case.
              </SectionHelp>
            </div>
          </div>

          <div className="fd-profile__row">
            <div className="fd-profile__label">Clinical comfort areas</div>
            <div className="fd-profile__value">
              <div style={{ display: "grid", gap: 8 }}>
                {CLINICAL_COMFORT_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={preferences.clinicalComfortAreas.includes(option.value)}
                      disabled={savingIntakeRules}
                      onChange={() => updateClinicalComfortArea(option.value)}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
              <SectionHelp>
                These are positive fit signals for the navigator. They do not
                exclude other patients.
              </SectionHelp>
            </div>
          </div>

          <div className="fd-profile__row">
            <div className="fd-profile__label">Geography preference</div>
            <div className="fd-profile__value">
              <select
                value={preferences.geographyPreference}
                disabled={savingIntakeRules}
                onChange={(e) => {
                  setPreferences((prev) => ({
                    ...prev,
                    geographyPreference: e.target.value as GeographyPreference,
                  }));
                  setIntakeRulesError(null);
                  setIntakeRulesSavedAt(null);
                }}
                style={{
                  width: "100%",
                  maxWidth: 420,
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid #d1d9e6",
                  fontFamily: "inherit",
                }}
              >
                {GEOGRAPHY_OPTIONS.map((opt) => (
                  <option key={opt.value || "none"} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <SectionHelp>
                The system will still calculate geography from postal code and
                clinic location. This only adds your preference.
              </SectionHelp>
            </div>
          </div>

          <div className="fd-profile__row">
            <div className="fd-profile__label">Navigator note</div>
            <div className="fd-profile__value">
              <textarea
                value={preferences.navigatorNote}
                disabled={savingIntakeRules}
                maxLength={500}
                rows={4}
                placeholder="Optional internal note for navigators. Example: strong fit for older adults who live close to clinic."
                onChange={(e) => {
                  setPreferences((prev) => ({
                    ...prev,
                    navigatorNote: e.target.value,
                  }));
                  setIntakeRulesError(null);
                  setIntakeRulesSavedAt(null);
                }}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid #d1d9e6",
                  resize: "vertical",
                  fontFamily: "inherit",
                }}
              />
              <SectionHelp>
                {preferences.navigatorNote.length}/500 characters. This is the
                only free-text field in Intake Rules.
              </SectionHelp>
            </div>
          </div>

          {intakeRulesError ? (
            <div className="fd-profile__row">
              <div className="fd-profile__label"></div>
              <div className="fd-profile__value" style={{ color: "#b00020" }}>
                {intakeRulesError}
              </div>
            </div>
          ) : null}

          <div className="fd-profile__row">
            <div className="fd-profile__label"></div>
            <div
              className="fd-profile__value"
              style={{ display: "flex", gap: 12, alignItems: "center" }}
            >
              <button
                className="fd-profile__btn"
                onClick={onSaveIntakeRules}
                disabled={savingIntakeRules}
              >
                {savingIntakeRules ? "Saving…" : "Save Intake Rules"}
              </button>

              {intakeRulesSavedAt ? (
                <span style={{ color: "#6b7a94", fontSize: 13 }}>
                  Saved {formatDateTime(intakeRulesSavedAt)}
                </span>
              ) : null}
            </div>
          </div>
        </>
      )}
    </details>
  );
}